import {
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  PROFILE_AVAILABILITY_STATUS,
  EMERGENCY_RELATIONSHIP_OPTIONS,
  normalizeWhatMattersMost,
  UK_COUNTRY,
} from "../../../constants/workerOnboarding";

function splitWorkerName(firstName, surname, fullName) {
  const first = (firstName || "").trim();
  const sur = (surname || "").trim();
  if (first || sur) {
    return { firstName: first, lastName: sur };
  }
  const raw = (fullName || "").trim();
  if (!raw) return { firstName: "", lastName: "" };
  const parts = raw.split(/\s+/);
  if (parts.length >= 2) {
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }
  return { firstName: raw, lastName: "" };
}

function normalizeEmergencyRelationship(value) {
  const v = (value || "").trim();
  if (!v) return "";
  if (EMERGENCY_RELATIONSHIP_OPTIONS.some((o) => o.value === v)) return v;
  const aliases = {
    sister: "Sibling",
    brother: "Sibling",
    mother: "Parent",
    father: "Parent",
    mum: "Parent",
    dad: "Parent",
    mom: "Parent",
    husband: "Spouse",
    wife: "Spouse",
    son: "Child",
    daughter: "Child",
    boyfriend: "Partner",
    girlfriend: "Partner",
  };
  return aliases[v.toLowerCase()] || v;
}

function formatSort(s) {
  const d = (s || "").replace(/\D/g, "").slice(0, 6);
  return d.length === 6 ? d.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3") : d;
}

export function buildCertificateEntriesFromCompliance(compliance) {
  const entries = [];
  let id = 1;
  const push = (slug, serviceNumber, existingFiles, adminMeta = {}) => {
    if (!slug) return;
    entries.push({
      id: id++,
      certSlug: slug,
      file: null,
      serviceNumber: serviceNumber || "",
      existingFiles: Array.isArray(existingFiles)
        ? existingFiles.filter(Boolean)
        : [],
      adminStatus: adminMeta.status ?? null,
      adminExpiryDate: adminMeta.expiryDate || "",
      adminIssueDate: adminMeta.issueDate || "",
      adminLastCheckedDate: adminMeta.lastCheckedDate || "",
    });
  };
  (compliance?.dbs || []).forEach((entry) => {
    push(
      entry?.dbsCert?.slug,
      entry?.certificateRef || entry?.serviceNumber || "",
      entry?.dbsFile,
      {
        status: entry?.status,
        expiryDate: entry?.expiryDate,
        issueDate: entry?.issueDate,
        lastCheckedDate: entry?.lastCheckedDate,
      }
    );
  });
  (compliance?.otherCerts || []).forEach((entry) => {
    const files = entry?.Files ?? entry?.File;
    push(entry?.Cert?.slug, entry?.serviceNumber || "", files, {
      status: entry?.status,
      expiryDate: entry?.expiryDate,
    });
  });
  if (!entries.length) {
    entries.push({
      id: 1,
      certSlug: "",
      file: null,
      serviceNumber: "",
      existingFiles: [],
    });
  }
  return entries;
}

export function getWorkerDisplayName(raw = {}) {
  const trimmed = (raw.fullName || "").trim();
  if (trimmed) return trimmed;
  const pi = raw.worker?.personalInformation ?? {};
  const first = (raw.firstName || pi.firstName || "").trim();
  const last = (
    raw.surName ??
    raw.lastName ??
    pi.surName ??
    pi.surname ??
    ""
  ).trim();
  return [first, last].filter(Boolean).join(" ");
}

export function getWorkerDashboardStatus(raw = {}, profileAvailabilityStatus) {
  const accountStatus = String(raw.status || "").toLowerCase();
  const isBlocked = !!raw.isBlockedByAdmin;
  const isAvailable =
    profileAvailabilityStatus === PROFILE_AVAILABILITY_STATUS.AVAILABLE;
  const isActive =
    accountStatus === "approved" && !isBlocked && isAvailable;
  return {
    isActive,
    isApproved: accountStatus === "approved",
    isBlocked,
    isAvailable,
  };
}

export function mapWorkerApiToFormValues(raw = {}) {
  const worker = raw.worker ?? {};

  const pi = worker.personalInformation ?? {};
  const ec = worker.emergencyContact ?? {};
  const rtw = worker.rightToWork ?? {};
  const tax = worker.employmentTax ?? {};
  const bank = worker.bankDetails ?? {};
  const wp = worker.workPreferences ?? {};
  const comp = worker.complianceSkills ?? {};

  const coords = pi?.baseLocation?.coordinates;
  const baseLat =
    Array.isArray(coords) &&
    coords.length === 2 &&
    typeof coords[1] === "number"
      ? String(coords[1])
      : pi.baseLat || "";
  const baseLng =
    Array.isArray(coords) &&
    coords.length === 2 &&
    typeof coords[0] === "number"
      ? String(coords[0])
      : pi.baseLng || "";

  const sectorsFromApi = Array.isArray(wp.sectors)
    ? wp.sectors.map((s) => ({
        sectorSlug: s?.sector?.slug || s?.sectorSlug || "",
        roles: Array.isArray(s?.roles)
          ? s.roles
              .map((r) => r?.role?.slug || r?.roleSlug || r?.slug || "")
              .filter(Boolean)
          : [],
      }))
    : [];

  const asArray = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
  const asOne = (v) => (v ? String(v) : "");
  const resolveBoolean = (primary, secondary) => {
    if (typeof primary === "boolean") return primary;
    if (typeof secondary === "boolean") return secondary;
    if (primary === "yes" || primary === "no") return primary === "yes";
    if (secondary === "yes" || secondary === "no") return secondary === "yes";
    return false;
  };

  const piNames = splitWorkerName(
    pi.firstName || raw.firstName,
    pi.surName ?? pi.surname ?? raw.surName ?? raw.lastName,
    pi.fullName || raw.fullName
  );
  const ecNames = splitWorkerName(
    ec.firstName,
    ec.surName ?? ec.surname,
    ec.fullName
  );
  const may = worker.moreAboutYou ?? {};
  const certEntries = buildCertificateEntriesFromCompliance(comp);
  const profileAvailabilityStatus =
    worker.status ??
    worker.profileAvailability?.status ??
    PROFILE_AVAILABILITY_STATUS.AVAILABLE;
  const profileAvailabilityUnavailableTill =
    worker.unavailableTill ??
    worker.profileAvailability?.unavailableTill ??
    "";

  let rightToWorkStatus = rtw.rightToWorkStatus || "";
  if (!rightToWorkStatus && resolveBoolean(rtw.isBritishOrIrish)) {
    rightToWorkStatus = RIGHT_TO_WORK_STATUS_BRITISH_IRISH;
  }

  return {
    role: "worker",
    personalInformation: {
      ...piNames,
      dob: pi.dob || "",
      gender: pi.gender || "",
      addressSearch: pi.currentAddress || "",
      addressLine1: pi.addressLine1 || pi.road || "",
      addressLine2: pi.addressLine2 ?? pi.address2 ?? pi.door ?? "",
      postcode: pi.postalCode || pi.postcode || "",
      city: pi.city || "",
      county: pi.county || "",
      country: pi.country || UK_COUNTRY,
      languagesSpoken: Array.isArray(wp.languages) ? wp.languages : [],
      phoneNumber: (pi.phoneNumber || raw.phoneNumber || "").replace(/^\+/, ""),
      email: pi.email || raw.email || "",
      nationalInsuranceNumber: pi.nationalInsuranceNumber || "",
      nationality: pi.nationality || rtw.nationality || "",
      baseLat,
      baseLng,
      jobarkIdSelfie: null,
      uploadSelfieUrl: asOne(pi.uploadSelfie),
    },
    emergencyContact: {
      ...ecNames,
      relationshipToWorker: normalizeEmergencyRelationship(
        ec.relationshipToWorker || ec.relationship || ""
      ),
      phoneNumber: (ec.phoneNumber || "").replace(/^\+/, ""),
      alternativePhoneNumber: (
        ec.alternativePhoneNumber ??
        ec.altPhoneNumber ??
        ""
      ).replace(/^\+/, ""),
    },
    rightToWork: {
      rightToWorkStatus,
      shareCode: rtw.homeShareSortCode || rtw.shareCode || "",
      visaExpiryDate: rtw.visaExpiryDate || "",
      drivingLicense: null,
      drivingLicenseUrl: asOne(rtw.drivingLicense),
      drivingLicenseBack: null,
      drivingLicenseBackUrl: asOne(rtw.drivingLicenseBack),
      dvlaShareCode: rtw.dvlaShareCode || "",
      drivingLicenseStatus: rtw.drivingLicenseStatus ?? null,
      drivingLicenseExpiryDate: rtw.drivingLicenseExpiryDate || "",
      licenseNumber: rtw.licenseNumber || "",
      uploadId: [],
      uploadIdUrls: asArray(rtw.uploadId),
      proofOfAddress: [],
      proofOfAddressUrls: asArray(rtw.proofOfAddress),
    },
    employmentTax: {
      hasP45:
        tax.hasP45 === true ? "yes" : tax.hasP45 === false ? "no" : "",
      p45Upload: null,
      p45FileUrls: asArray(tax.p45Files),
      starterChecklistChoice: tax.starterChecklistChoice || "",
      hasPostgraduateLoan:
        tax.hasPostgraduateLoan === true
          ? "yes"
          : tax.hasPostgraduateLoan === false
          ? "no"
          : "",
      planType: tax.planType || "",
      acceptEmergencyTaxCode: tax.acceptEmergencyTaxCode === true,
      notes: tax.notesField || tax.notes || "",
    },
    bankDetails: {
      accountHolderName: bank.accountHolderName || "",
      sortCode: bank.sortCode ? formatSort(bank.sortCode) : "",
      accountNumber: bank.accountNumber || "",
      bankName: bank.bankName || "",
    },
    moreAboutYou: {
      healthAccessibilityDetails: may.healthAccessibilityAdjustments || "",
      hasNoConvictions: !resolveBoolean(
        may.hasUnspentConvictionsOrPendingCases
      ),
      hasConvictionsOrPending: resolveBoolean(
        may.hasUnspentConvictionsOrPendingCases
      ),
      criminalConvictionsDetails: may.criminalConvictionsDetails || "",
      aboutYouWorkHistory: may.aboutYouAndWorkHistory || "",
    },
    workPreferences: {
      sectors: sectorsFromApi.length
        ? sectorsFromApi
        : [{ sectorSlug: "", roles: [] }],
      preferredWorkRadius:
        typeof wp.preferredWorkRadius === "number"
          ? wp.preferredWorkRadius
          : "",
      availability: Array.isArray(wp.availability) ? wp.availability : [],
      transport: Array.isArray(wp.transport)
        ? wp.transport
            .map((mode) => (mode === "public_transport" ? "public" : mode))
            .filter(Boolean)
        : [],
      whatMattersMost: normalizeWhatMattersMost(wp.whatMattersMost),
      optOut48HourLimit: !!wp.optOut48HourLimit,
    },
    certificateEntries: certEntries,
    profileAvailability: {
      status:
        profileAvailabilityStatus === PROFILE_AVAILABILITY_STATUS.UNAVAILABLE
          ? PROFILE_AVAILABILITY_STATUS.UNAVAILABLE
          : PROFILE_AVAILABILITY_STATUS.AVAILABLE,
      unavailableTill: profileAvailabilityUnavailableTill || "",
    },
  };
}
